import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Alert, Typography, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import workoutService from '../services/workoutService';

const PAGE_SIZE = 10;

const Referrals = () => {
  const [rows, setRows] = useState([]);
  const [filterBy, setFilterBy] = useState('name');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [alertMsg, setAlertMsg] = useState(null);
  const [editingReferralId, setEditingReferralId] = useState(null);
  const [newReferralValue, setNewReferralValue] = useState('');
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [newPeriodStart, setNewPeriodStart] = useState('');
  const [newPeriodEnd, setNewPeriodEnd] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await workoutService.getAdminReferrals();
        setRows(data);
      } catch (err) {
        setAlertMsg('Failed to load referrals. Please try again.');
      }
    };
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    let list = [...rows];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        if (filterBy === 'email') return item.referrerEmail.toLowerCase().includes(q) || item.athleteEmail.toLowerCase().includes(q);
        return item.referrerName.toLowerCase().includes(q) || item.athleteName.toLowerCase().includes(q);
      });
    }
    if (statusFilter !== 'all') {
      list = list.filter((item) => statusFilter === 'active' ? item.status.toLowerCase() === 'active' : item.status.toLowerCase() === 'expired');
    }
    if (fromDate || toDate) {
      list = list.filter((item) => {
        const start = new Date(item.periodStart);
        if (fromDate && start < new Date(fromDate)) return false;
        if (toDate && start > new Date(toDate)) return false;
        return true;
      });
    }
    return list;
  }, [rows, filterBy, statusFilter, search, fromDate, toDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(startIndex, startIndex + PAGE_SIZE);

  const isFiltered = !!search.trim() || statusFilter !== 'all' || fromDate || toDate;

  const handleClear = () => {
    setFilterBy('name'); setStatusFilter('all'); setSearch(''); setFromDate(''); setToDate(''); setPage(1);
  };

  const handleExport = () => {
    if (!filtered.length) { setAlertMsg('No referrals to export.'); return; }
    const headers = ['No', 'Referrer Name', 'Referrer Role', 'Referrer Email', 'Referred Athlete', 'Athlete Email', 'Referral %', 'Period Start', 'Period End', 'Status', 'Athlete Total Earnings', 'Referrer Share Earned'];
    const csvRows = filtered.map((r, idx) => [
      idx + 1, r.referrerName, r.referrerRole === 'trainer' ? 'Athlete' : r.referrerRole === 'trainee' ? 'User' : (r.referrerRole || ''), r.referrerEmail, r.athleteName, r.athleteEmail,
      r.referralPercent, r.periodStart, r.periodEnd, r.status, r.athleteTotalEarnings, r.referrerShareEarned
    ]);
    const csv = [headers, ...csvRows].map((row) => row.map((v) => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'referrals.csv');
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Field</InputLabel>
          <Select value={filterBy} label="Field" onChange={(e) => setFilterBy(e.target.value)}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 180 }} />
        <Typography variant="caption" sx={{ color: '#8b949e', mx: 0.5 }}>From:</Typography>
        <TextField size="small" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <Typography variant="caption" sx={{ color: '#8b949e' }}>–</Typography>
        <TextField size="small" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={() => setPage(1)}>Search</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleClear} disabled={!isFiltered}>Clear</Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>Export</Button>
        <IconButton size="small" onClick={() => { setPage(1); setAlertMsg('Referrals refreshed.'); }} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
      </Paper>

      {alertMsg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setAlertMsg(null)}>{alertMsg}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Referrer Name</TableCell>
                <TableCell>Referrer Role</TableCell>
                <TableCell>Referrer Email</TableCell>
                <TableCell>Referred Athlete</TableCell>
                <TableCell>Athlete Email</TableCell>
                <TableCell>Referral %</TableCell>
                <TableCell>Period Start</TableCell>
                <TableCell>Period End</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Athlete Earnings</TableCell>
                <TableCell>Referrer Earned</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageItems.length === 0 ? (
                <TableRow><TableCell colSpan={13} align="center" sx={{ py: 4, color: '#8b949e' }}>No referrals found.</TableCell></TableRow>
              ) : pageItems.map((r, idx) => (
                <TableRow key={r.id}>
                  <TableCell>{startIndex + idx + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.referrerName}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.referrerRole === 'trainer' ? 'Athlete' : r.referrerRole === 'trainee' ? 'User' : (r.referrerRole || '—')}
                      size="small"
                      sx={{
                        bgcolor: r.referrerRole === 'trainer' ? 'rgba(43,182,169,0.15)' : 'rgba(144,202,249,0.15)',
                        color: r.referrerRole === 'trainer' ? '#2bb6a9' : '#90caf9',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{r.referrerEmail}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{r.athleteName}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{r.athleteEmail}</TableCell>

                  {/* Referral % */}
                  <TableCell>
                    {editingReferralId === r.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField size="small" type="number" value={newReferralValue} onChange={(e) => setNewReferralValue(e.target.value)} sx={{ width: 70 }} inputProps={{ min: 0, max: 100 }} />
                        <IconButton size="small" color="success" onClick={async () => {
                          const valueNum = Number(newReferralValue);
                          if (Number.isNaN(valueNum) || valueNum < 0 || valueNum > 100) { setAlertMsg('Referral % must be between 0 and 100.'); return; }
                          try {
                            await workoutService.updateReferralCommission(r.athleteId, valueNum);
                            setRows((prev) => prev.map((row) => row.id === r.id ? { ...row, referralPercent: valueNum } : row));
                            setEditingReferralId(null);
                            setAlertMsg('Referral % updated successfully.');
                          } catch (err) { setAlertMsg('Failed to update referral %. Please try again.'); }
                        }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" onClick={() => setEditingReferralId(null)} sx={{ color: '#8b949e' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{r.referralPercent}%</span>
                        <IconButton size="small" onClick={() => { setEditingReferralId(r.id); setNewReferralValue(String(r.referralPercent ?? '')); }} sx={{ color: '#8b949e', p: 0.3 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                      </Box>
                    )}
                  </TableCell>

                  {/* Period Start */}
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {editingPeriodId === r.id
                      ? <TextField size="small" type="date" value={newPeriodStart} onChange={(e) => setNewPeriodStart(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
                      : r.periodStart}
                  </TableCell>

                  {/* Period End */}
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {editingPeriodId === r.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField size="small" type="date" value={newPeriodEnd} onChange={(e) => setNewPeriodEnd(e.target.value)} sx={{ width: 140 }} InputLabelProps={{ shrink: true }} />
                        <IconButton size="small" color="success" onClick={async () => {
                          if (!newPeriodStart || !newPeriodEnd) { setAlertMsg('Please select both period start and end.'); return; }
                          if (new Date(newPeriodStart) > new Date(newPeriodEnd)) { setAlertMsg('Period start must be before period end.'); return; }
                          try {
                            await workoutService.updateReferralPeriod(r.athleteId, newPeriodStart, newPeriodEnd);
                            setRows((prev) => prev.map((row) => row.id === r.id ? { ...row, periodStart: newPeriodStart, periodEnd: newPeriodEnd } : row));
                            setEditingPeriodId(null);
                            setAlertMsg('Period updated successfully.');
                          } catch (err) { setAlertMsg('Failed to update referral period.'); }
                        }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                        <IconButton size="small" onClick={() => setEditingPeriodId(null)} sx={{ color: '#8b949e' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                      </Box>
                    ) : r.periodEnd}
                  </TableCell>

                  <TableCell>
                    <Chip label={r.status} size="small"
                      sx={{ bgcolor: r.status.toLowerCase() === 'active' ? 'rgba(76,175,80,0.15)' : 'rgba(139,148,158,0.15)',
                        color: r.status.toLowerCase() === 'active' ? '#81c784' : '#8b949e', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>${r.athleteTotalEarnings.toFixed(2)}</TableCell>
                  <TableCell>${r.referrerShareEarned.toFixed(2)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => {
                        if (editingPeriodId === r.id) return; // already editing via save button
                        setEditingPeriodId(r.id);
                        setNewPeriodStart(r.periodStart || '');
                        setNewPeriodEnd(r.periodEnd || '');
                      }} sx={{ color: '#8b949e' }}><CalendarMonthIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setAlertMsg('Delete referral: backend wiring coming soon.')} sx={{ color: '#8b949e' }}><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#8b949e' }}>
            Showing {startIndex + 1} to {Math.min(startIndex + PAGE_SIZE, filtered.length)} of {filtered.length} entries
          </Typography>
          <Pagination count={totalPages} page={currentPage} onChange={(_, v) => setPage(v)} size="small"
            sx={{ '& .MuiPaginationItem-root': { color: '#8b949e' }, '& .Mui-selected': { bgcolor: 'rgba(43,182,169,0.15) !important', color: '#2bb6a9' } }} />
        </Box>
      )}
    </Box>
  );
};

export default Referrals;
